import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { BackgroundProcess } from 'src/data-capture/hooks/file-uploads/types';

export enum MeetingNoteStatus {
  READY = 'READY',
  MEETING_NOTE_PROCESSING = 'MEETING_NOTE_PROCESSING',
  TRANSCRIPTION_PROCESSING = 'TRANSCRIPTION_PROCESSING',
  FAILED = 'FAILED',
  NOT_CREATED = 'NOT_CREATED',
}

type MeetingNote = {
  id?: string;
  status: MeetingNoteStatus;
  markdownContent?: string;
  taggedMarkdownContent?: string;
  templatisedFileId?: number;
  templateId?: string;
  backgroundProcess?: BackgroundProcess;
};

type UseCalendarEventMeetingNoteResult = {
  meetingNote: MeetingNote | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

export const useCalendarEventMeetingNote = (
  eventId: string,
  { enabled }: { enabled: boolean },
): UseCalendarEventMeetingNoteResult => {
  const { data, isLoading, isError, error, refetch } = useQuery<MeetingNote>({
    queryKey: ['calendar-events', eventId, 'meeting-notes'],
    enabled,
    retry: false,
    refetchInterval: query => {
      const status = query.state.data?.backgroundProcess?.status;
      if (status === 'PENDING' || status === 'PROCESSING') {
        return 2000;
      }
      return false;
    },
  });

  const prevStatus = useRef(data?.backgroundProcess?.status);
  useEffect(() => {
    const status = data?.backgroundProcess?.status;
    if (status === 'SUCCESS' && prevStatus.current !== 'SUCCESS') {
      dispatchAppEvent('MEETING_NOTE_GENERATED');
    }
    prevStatus.current = status;
  }, [data?.backgroundProcess?.status]);

  return {
    meetingNote: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
